import { useQueryClient } from '@gogame/query';
import { useRef } from 'react';

/**
 * Hook for typewriter animation for bot messages in chat.
 * Animates originalText for up to 10 seconds, from fast per-char to larger steps.
 * After 10 seconds, shows all at once. Uses linear step growth.
 */
export function useTypingAnimation() {
  const queryClient = useQueryClient();
  const typingTimers = useRef({});

  const MAX_DURATION = 10000; // ms
  const MIN_INTERVAL = 20; // ms
  const MAX_INTERVAL = 60; // ms

  const startTypingAnimation = ({ queryKey, jobId }) => {
    const update = () => {
      queryClient.setQueriesData(
        { queryKey: queryKey, exact: false },
        (old = []) => {
          if (!Array.isArray(old)) return old;
          return old.map((el) => {
            if (el.jobId === jobId && typeof el.originalText === 'string') {
              const total = el.originalText.length;
              const current = el.typingIterator || 0;

              // If more than 10 seconds have passed or the text is almost finished — show everything immediately
              if (
                el._typingStart &&
                Date.now() - el._typingStart > MAX_DURATION
              ) {
                const {
                  typingIterator,
                  originalText,
                  _typingStart,
                  _typingInterval,
                  ...rest
                } = el;
                return { ...rest, text: el.originalText };
              }

              // Linearly increase the step: from 1 character to total/10 over 10 seconds
              const start = el._typingStart || Date.now();
              const elapsed = Date.now() - start;
              const progress = Math.min(elapsed / MAX_DURATION, 1);
              const maxStep = Math.max(Math.floor(total / 10), 1);
              const step = Math.max(
                1,
                Math.floor(1 + (maxStep - 1) * progress)
              );
              const interval = Math.floor(
                MAX_INTERVAL - (MAX_INTERVAL - MIN_INTERVAL) * progress
              );

              const nextIterator = Math.min(current + step, total);
              const nextText = el.originalText.slice(0, nextIterator);

              if (nextIterator >= total) {
                const {
                  typingIterator,
                  originalText,
                  _typingStart,
                  _typingInterval,
                  ...rest
                } = el;
                return { ...rest, text: el.originalText };
              }
              return {
                ...el,
                typingIterator: nextIterator,
                text: nextText,
                _typingStart: start,
                _typingInterval: interval,
              };
            }
            return el;
          });
        }
      );

      const current = queryClient.getQueryData(queryKey);
      const target = Array.isArray(current)
        ? current.find((el) => el.jobId === jobId)
        : null;
      if (
        target &&
        target.originalText &&
        (target.typingIterator || 0) < target.originalText.length
      ) {
        const interval = target._typingInterval || MIN_INTERVAL;
        typingTimers.current[jobId] = setTimeout(update, interval);
      } else {
        clearTimeout(typingTimers.current[jobId]);
        delete typingTimers.current[jobId];
      }
    };
    if (typingTimers.current[jobId]) clearTimeout(typingTimers.current[jobId]);
    update();
  };

  return startTypingAnimation;
}
